import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, TrendingUp, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { AnnotationBanner } from "@/components/Annotation";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fM = (v) => v ? `${v.toFixed(1)}M` : "0";
const fPct = (v) => v ? `${v.toFixed(1)}%` : "0%";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
      <p className="text-slate-300 font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-mono">{p.name}: INR {p.value?.toFixed(1)}M</p>
      ))}
    </div>
  );
};

export default function FinancialOverview() {
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/financial/pnl`).then((r) => { setPnl(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading || !pnl?.data) return <div className="h-64 skeleton-shimmer rounded" />;

  const d = pnl.data;
  const periods = pnl.periods || [];
  const labels = pnl.labels || [];

  // Bar chart data
  const chartData = labels.map((label, i) => ({
    period: label,
    revenue: d.net_revenue[periods[i]] || 0,
    grossProfit: d.gross_profit[periods[i]] || 0,
    ebitda: d.ebitda[periods[i]] || 0,
    pat: d.pat[periods[i]] || 0,
  }));

  // P&L rows
  const rows = [
    { label: "Gross Revenue", key: "gross_revenue", bold: true },
    { label: "  Online", key: "online_revenue", indent: true },
    { label: "  Offline", key: "offline_revenue", indent: true },
    { label: "Returns", key: "returns", negative: true },
    { label: "Net Revenue", key: "net_revenue", bold: true, highlight: true },
    { label: "COGS", key: "cogs", negative: true },
    { label: "Gross Profit", key: "gross_profit", bold: true },
    { label: "Gross Margin %", key: "gross_margin", pct: true, color: "text-blue-400" },
    { label: "Freight", key: "freight", negative: true },
    { label: "CM1", key: "cm1", bold: true },
    { label: "CM1 Margin %", key: "cm1_margin", pct: true, color: "text-blue-400" },
    { label: "Advertising", key: "advertising", negative: true },
    { label: "Marketplace Commission", key: "marketplace_commission", negative: true },
    { label: "CM2", key: "cm2", bold: true },
    { label: "CM2 Margin %", key: "cm2_margin", pct: true, color: "text-blue-400" },
    { label: "Employee Expense", key: "employee_expense", negative: true },
    { label: "Rent (Offline)", key: "rent_offline", negative: true },
    { label: "Brand Ambassador", key: "brand_ambassador", negative: true },
    { label: "Brand Building", key: "brand_building", negative: true },
    { label: "Other Expenses", key: "other_expenses", negative: true },
    { label: "EBITDA", key: "ebitda", bold: true, highlight: true },
    { label: "EBITDA Margin %", key: "ebitda_margin", pct: true, color: "text-emerald-400" },
    { label: "Depreciation", key: "depreciation", negative: true },
    { label: "Interest", key: "interest", negative: true },
    { label: "PBT", key: "pbt", bold: true },
    { label: "Tax", key: "tax", negative: true },
    { label: "PAT", key: "pat", bold: true, highlight: true },
    { label: "PAT Margin %", key: "pat_margin", pct: true, color: "text-amber-400" },
  ];

  return (
    <div className="space-y-5" data-testid="financial-pnl-page">
      <div>
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">P&L Statement</h1>
        <p className="text-sm text-slate-400 mt-1">Consolidated Income Statement &middot; FY2024A to FY2030E &middot; INR Millions</p>
      </div>

      {/* Revenue & Profit Chart */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="pnl-chart">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-400" />Revenue, Gross Profit, EBITDA & PAT
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" name="Net Revenue" fill="#3b82f6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="grossProfit" name="Gross Profit" fill="#10b981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="ebitda" name="EBITDA" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              <Bar dataKey="pat" name="PAT" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <AnnotationBanner title="Financial Highlights">
        <p>Revenue projected to grow from INR {fM(d.net_revenue.FY2025A)} (FY25) to INR {fM(d.net_revenue.FY2030E)} (FY30) — a {((Math.pow(d.net_revenue.FY2030E / d.net_revenue.FY2025A, 0.2) - 1) * 100).toFixed(0)}% CAGR over 5 years. EBITDA margin expands from {fPct(d.ebitda_margin.FY2025A)} to {fPct(d.ebitda_margin.FY2030E)}, driven by operating leverage, scale efficiencies, and the high-margin D2C website channel growing as a % of total revenue.</p>
      </AnnotationBanner>

      {/* P&L Table */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="pnl-table">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200">Detailed P&L (INR Millions)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold w-52">Particulars</th>
                {labels.map((l) => (
                  <th key={l} className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">{l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className={`border-b border-slate-800/30 ${row.highlight ? "bg-slate-800/20" : ""} hover:bg-slate-800/20 transition-colors`}>
                  <td className={`py-2 px-3 ${row.bold ? "font-semibold text-slate-200" : row.indent ? "text-slate-500 pl-6" : "text-slate-400"} ${row.color || ""}`}>
                    {row.label}
                  </td>
                  {periods.map((p) => {
                    const v = d[row.key]?.[p];
                    return (
                      <td key={p} className={`py-2 px-3 text-right font-mono text-xs ${
                        row.bold ? "font-semibold text-slate-200" :
                        row.pct ? (row.color || "text-slate-400") :
                        row.negative ? "text-slate-400" : "text-slate-300"
                      }`}>
                        {row.pct ? fPct(v) : v ? `${v.toFixed(1)}` : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
